import { fieldErrorMessage, problemMessage } from '@masar/i18n';
import { useI18n } from '@masar/i18n/react';
import { useCallback } from 'react';
import { Icon } from './icons/Icon';

interface ProblemLike {
  code?: unknown;
  title?: unknown;
  status?: unknown;
  fieldErrors?: unknown;
  params?: unknown;
}

function asProblem(e: unknown): ProblemLike | null {
  return typeof e === 'object' && e !== null ? e : null;
}

/**
 * Turns any thrown error (an ApiError from @masar/api-client, a network
 * failure, anything) into one sentence in the current language.
 */
export function useErrorText() {
  const { locale } = useI18n();
  return useCallback(
    (e: unknown): string => {
      const p = asProblem(e);
      const code = typeof p?.code === 'string' ? p.code : undefined;
      const title = typeof p?.title === 'string' && p.title ? p.title : undefined;
      if (code === 'validation_failed' || code === 'bad_request') {
        const fe = Array.isArray(p?.fieldErrors)
          ? (p.fieldErrors as { code?: unknown; params?: unknown }[])
          : [];
        const first = fe[0];
        if (first && typeof first.code === 'string') {
          return fieldErrorMessage(
            locale,
            first.code,
            (first.params ?? undefined) as Record<string, unknown> | undefined,
          );
        }
      }
      const params =
        typeof p?.params === 'object' && p.params !== null
          ? (p.params as Record<string, unknown>)
          : undefined;
      return problemMessage(locale, code ?? 'internal', title, params);
    },
    [locale],
  );
}

/** The error under a form or an action, announced to screen readers. */
export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  const text = useErrorText();
  if (!error) return null;
  return (
    <p
      role="alert"
      className={className}
      style={{ color: 'var(--crit)', fontSize: 13.5, display: 'flex', gap: 8, alignItems: 'center' }}
    >
      <Icon name="alert" size="sm" />
      {text(error)}
    </p>
  );
}

/** Field error text for a dotted path from an ApiError-like value (or undefined). */
export function useFieldError() {
  const { locale } = useI18n();
  return useCallback(
    (e: unknown, path: string): string | undefined => {
      const p = asProblem(e);
      if (!p || !Array.isArray(p.fieldErrors)) return undefined;
      const hit = (p.fieldErrors as { field?: unknown; code?: unknown; params?: unknown }[]).find(
        (f) => f.field === path,
      );
      if (!hit || typeof hit.code !== 'string') return undefined;
      return fieldErrorMessage(
        locale,
        hit.code,
        (hit.params ?? undefined) as Record<string, unknown> | undefined,
      );
    },
    [locale],
  );
}
